<template>
  <div v-if="isLoading()" class="loader center-middle">
    <LoadingState :size="50" />
  </div>
  <div v-else-if="isErrored()">
    <ErrorState />
  </div>
  <form v-else-if="packages" @submit.prevent="submit">
    <div class="pb-3">
      <span>{{ $t("global.advanced-auto-scale-service-interruption-note-1") }}</span>
      <strong class="mx-1">
        {{ $t("global.advanced-auto-scale-service-interruption-note-2") }}
      </strong>
      <span>{{ $t("global.advanced-auto-scale-service-interruption-note-3") }}</span>
      <a :href="KB_LINK" target="_blank" class="pl-1">{{
        $t("global.advanced-auto-scale-service-interruption-note-4")
      }}</a>
      .
    </div>
    <FormMessage :errors="errors" v-if="errors" />
    <FormAdvancedAutoScalePackageSelect
      v-model="fields.addonId"
      :packages="packages"
      :disabled="isSubmitting"
    />
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-enable-site-site-auto-scale-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="enable-site-advanced-auto-scale-button"
      >
        {{ $t("global.turn-on") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import ErrorState from "@/components/display/ItemErrorState";
import FormAdvancedAutoScalePackageSelect from "@/components/display/form/FormAdvancedAutoScalePackageSelect";
import FormMessage from "@/components/display/form/FormMessage";
import LoadingState from "@/components/display/LoadingState";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { enableSiteAdvancedAutoScale as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "SiteEnableAdvancedAutoScaleForm",
  props: {
    plan: {
      type: Object,
      required: true,
    },
    site: {
      type: Object,
      required: true,
    },
  },
  components: {
    Btn,
    ErrorState,
    FormAdvancedAutoScalePackageSelect,
    FormMessage,
    LoadingState,
  },
  setup(props, { emit, root }) {
    const KB_LINK = "https://help.nexcess.net/client-portal/what-is-advanced-auto-scaling";
    const isSubmitting = ref(false);
    const errors = ref(null);
    const fields = reactive({
      addonId: null,
      version: props.site?.environment?.software?.php?.version,
      scale: 0,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        addonId: {
          required: root.$t("validation.package-please-select"),
        },
      }
    );

    const cancel = () => emit("close");
    const isErrored = () => status.value.isErrored;
    const isLoading = () => status.value.isLoading;
    const isSubmittable = () => !isSubmitting.value && validation.errors.length === 0;
    const packages = computed(() => store.getters["sites/getAdvancedAutoScalePackages"]);
    const status = computed(() => store.getters["sites/getAdvancedAutoScalePackagesStatus"]);

    const submit = () => {
      errors.value = null;
      validation
        .validate()
        .then(() => {
          isSubmitting.value = true;
          const name = props.plan.isTShirt ? props.plan.nickname : props.site.domain;
          store
            .dispatch("sites/enableAdvancedAutoScale", {
              id: props.site.id,
              data: {
                addonId: fields.addonId,
                version: fields.version,
                scale: fields.scale,
              },
            })
            .then(() => {
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.advanced-auto-scale-enabling-message", { name }),
              });
              emit("close");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.advanced-auto-scale-enable-error-message", { name }),
              });
            })
            .finally(() => {
              isSubmitting.value = false;
            });
        })
        .catch(() => {});
    };

    onMounted(() => {
      store.dispatch("sites/getAdvancedAutoScalePackages", props.site.id);
    });

    return {
      cancel,
      errors,
      fields,
      isErrored,
      isLoading,
      isSubmittable,
      isSubmitting,
      KB_LINK,
      packages,
      submit,
      validation,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
